import { useState } from 'react';
import { Download, Trash2, FolderInput, MessageSquare, Settings } from 'lucide-react';
import { useI18n } from '../i18n';
import type { ImageMetadata } from '../services/metadata';

interface ImageMetadataViewerProps {
    metadata?: ImageMetadata | null;
    className?: string;
    filename?: string;
    path?: string;
    imageUrl?: string;
    onAiTransfer?: (mode: 'image' | 'prompt' | 'both') => void;
    onApplySettings?: () => void;
    onDelete?: () => void;
    onMove?: () => void;
}

export default function ImageMetadataViewer({
    metadata,
    className = '',
    filename,
    path,
    imageUrl,
    onAiTransfer,
    onApplySettings,
    onDelete,
    onMove,
}: ImageMetadataViewerProps) {
    const { t } = useI18n();
    const [showAiMenu, setShowAiMenu] = useState(false);

    const handleDownload = async () => {
        if (!imageUrl) return;
        try {
            const response = await fetch(imageUrl, {
                credentials: 'include',
            });
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'image';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
            // Fallback: open in new tab
            window.open(imageUrl, '_blank');
        }
    };

    const renderAiButton = () => {
        if (!onAiTransfer) return null;

        return (
            <div className="relative">
                <button
                    onClick={() => setShowAiMenu(!showAiMenu)}
                    className={`w-full py-2 bg-gradient-to-r from-accent-600 to-indigo-600 hover:from-accent-500 hover:to-indigo-500 text-white rounded-lg flex items-center justify-center gap-2 transition-all shadow-lg hover:shadow-accent-500/30 ${showAiMenu ? 'ring-2 ring-white/20' : ''}`}
                >
                    <MessageSquare className="w-4 h-4" />
                    {t.gallery.ai_analyze}
                    <span className="text-xs opacity-70">▼</span>
                </button>

                {showAiMenu && (
                    <div className="absolute top-full left-0 right-0 mt-2 bg-dark-700 border border-dark-600 rounded-lg shadow-xl z-50 overflow-hidden flex flex-col">
                        <button
                            onClick={() => { onAiTransfer('image'); setShowAiMenu(false); }}
                            className="px-4 py-3 text-left text-dark-200 hover:bg-dark-600 hover:text-white transition-colors flex items-center gap-2 border-b border-dark-600"
                        >
                            🖼️ {t.gallery.mode_image}
                        </button>
                        <button
                            onClick={() => { onAiTransfer('prompt'); setShowAiMenu(false); }}
                            className="px-4 py-3 text-left text-dark-200 hover:bg-dark-600 hover:text-white transition-colors flex items-center gap-2 border-b border-dark-600"
                        >
                            📝 {t.gallery.mode_prompt}
                        </button>
                        <button
                            onClick={() => { onAiTransfer('both'); setShowAiMenu(false); }}
                            className="px-4 py-3 text-left text-yellow-200 hover:bg-dark-600 hover:text-yellow-100 transition-colors flex items-center gap-2"
                        >
                            ✨ {t.gallery.mode_both}
                        </button>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className={`space-y-3 text-sm ${className}`}>
            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-2">
                <button
                    onClick={handleDownload}
                    className="py-2 bg-dark-700 hover:bg-dark-600 text-dark-200 hover:text-white rounded-lg flex items-center justify-center gap-2 transition-colors border border-dark-600"
                >
                    <Download className="w-4 h-4" /> {t.common.download}
                </button>
                {onMove && (
                    <button
                        onClick={onMove}
                        className="py-2 bg-dark-700 hover:bg-dark-600 text-dark-200 hover:text-white rounded-lg flex items-center justify-center gap-2 transition-colors border border-dark-600"
                    >
                        <FolderInput className="w-4 h-4" /> {t.gallery.move}
                    </button>
                )}
            </div>

            {renderAiButton()}

            {onApplySettings && metadata && (
                <button
                    onClick={onApplySettings}
                    className="w-full py-2 bg-purple-600/20 hover:bg-purple-600/30 text-purple-200 hover:text-purple-100 rounded-lg flex items-center justify-center gap-2 transition-colors border border-purple-500/30"
                >
                    <Settings className="w-4 h-4" /> {t.gallery.apply_settings}
                </button>
            )}

            {onDelete && (
                <button
                    onClick={onDelete}
                    className="w-full py-2 bg-red-600/20 hover:bg-red-600/30 text-red-200 hover:text-red-100 rounded-lg flex items-center justify-center gap-2 transition-colors border border-red-500/30"
                >
                    <Trash2 className="w-4 h-4" /> {t.common.delete}
                </button>
            )}

            {/* Metadata display */}
            {path && (
                <div>
                    <span className="text-dark-500">{t.gallery.path}</span>
                    <p className="text-dark-300 mt-1 break-all text-xs font-mono select-all bg-dark-800 p-1 rounded">
                        {path}
                    </p>
                </div>
            )}

            {!metadata && filename && (
                <div>
                    <span className="text-dark-500">{t.gallery.filename}</span>
                    <p className="text-dark-300 mt-1 break-all">{filename}</p>
                    <p className="text-dark-500 text-xs mt-4">{t.gallery.no_metadata}</p>
                </div>
            )}

            {metadata && (
                <>
                    <div>
                        <span className="text-dark-500">{t.gallery.model}</span>
                        <p className="text-dark-300 mt-1">{metadata.model}</p>
                    </div>

                    {metadata.seed !== undefined && metadata.seed > 0 && (
                        <div>
                            <span className="text-dark-500">{t.gallery.seed}</span>
                            <p className="text-dark-300 mt-1 font-mono">{metadata.seed}</p>
                        </div>
                    )}

                    {metadata.width && metadata.height && metadata.width > 0 && metadata.height > 0 && (
                        <div>
                            <span className="text-dark-500">{t.gallery.size}</span>
                            <p className="text-dark-300 mt-1">{metadata.width} x {metadata.height}</p>
                        </div>
                    )}

                    {((metadata.steps && metadata.steps > 0) || (metadata.scale && metadata.scale > 0)) && (
                        <div className="grid grid-cols-2 gap-2">
                            {metadata.steps && metadata.steps > 0 && (
                                <div>
                                    <span className="text-dark-500">Steps</span>
                                    <p className="text-dark-300 mt-1">{metadata.steps}</p>
                                </div>
                            )}
                            {metadata.scale && metadata.scale > 0 && (
                                <div>
                                    <span className="text-dark-500">Scale</span>
                                    <p className="text-dark-300 mt-1">{metadata.scale}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {metadata.sampler && (
                        <div>
                            <span className="text-dark-500">{t.gallery.sampler}</span>
                            <p className="text-dark-300 mt-1">{metadata.sampler}</p>
                        </div>
                    )}

                    {metadata.prompt && metadata.prompt.trim() && (
                        <div>
                            <span className="text-dark-500">{t.gallery.prompt}</span>
                            <p className="text-dark-300 mt-1 text-xs whitespace-pre-wrap bg-dark-800 p-2 rounded max-h-40 overflow-y-auto">
                                {metadata.prompt}
                            </p>
                        </div>
                    )}

                    {metadata.negativePrompt && metadata.negativePrompt.trim() && (
                        <div>
                            <span className="text-dark-500">{t.gallery.negative_prompt}</span>
                            <p className="text-dark-300 mt-1 text-xs whitespace-pre-wrap bg-dark-800 p-2 rounded max-h-40 overflow-y-auto">
                                {metadata.negativePrompt}
                            </p>
                        </div>
                    )}

                    {metadata.characterPrompts && metadata.characterPrompts.length > 0 && (
                        <div>
                            <span className="text-dark-500">Characters ({metadata.characterPrompts.length})</span>
                            <div className="mt-1 space-y-2">
                                {metadata.characterPrompts.map((char, idx) => (
                                    <div key={char.id || idx} className="bg-dark-800 p-2 rounded text-xs">
                                        <div className="text-accent-400 font-medium mb-1">Character {idx + 1}</div>
                                        {char.prompt && <p className="text-dark-300 whitespace-pre-wrap">{char.prompt}</p>}
                                        {char.uc && <p className="text-dark-500 mt-1 whitespace-pre-wrap">UC: {char.uc}</p>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
